import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns an EntityFormConfig for ERP Endpoint create or edit mode.
 *
 * @param mode - 'create' or 'edit'
 * @param registrySlug - The slug of the parent ERP registry
 * @param endpointSlug - Required for edit mode; the slug of the endpoint being edited
 */
export function getErpEndpointFormConfig(
  mode: 'create' | 'edit',
  registrySlug: string,
  endpointSlug?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';
  const collectionPath = `/api/admin/erp/registries/${registrySlug}/endpoints`;
  const itemPath = endpointSlug ? `${collectionPath}/${endpointSlug}` : collectionPath;

  return {
    id: isEdit ? createFormEditId('erp-endpoint', endpointSlug ?? 'unknown') : createFormCreateId('erp-endpoint'),

    endpoint: createFormEndpoint(
      isEdit,
      collectionPath,
      isEdit ? itemPath : collectionPath,
      !!endpointSlug,
      [
        'slug', 'name', 'description', 'http_method', 'path',
        'path_parameters', 'query_parameters', 'headers',
        'request_schema', 'request_transform', 'response_schema', 'response_transform',
        'example_params',
        'timeout_ms', 'is_active',
      ],
    ),

    fields: {
      slug: {
        type: 'text',
        label: 'Slug',
        placeholder: 'e.g. get-users',
        helperText: 'Lowercase letters, numbers, and hyphens only',
        section: 'basic',
        order: 1,
        validation: {
          required: true,
          max: { value: 100, message: 'Slug must be 100 characters or fewer' },
          pattern: {
            value: '^[a-z0-9-]+$',
            message: 'Only lowercase letters, numbers, and hyphens are allowed',
          },
        },
      },
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. Get Users',
        section: 'basic',
        order: 2,
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be 255 characters or fewer' },
        },
      },
      description: {
        type: 'textarea',
        label: 'Description',
        placeholder: 'Describe what this endpoint does...',
        section: 'basic',
        order: 3,
        options: {
          rows: 3,
          resize: 'vertical',
        },
      },
      http_method: {
        type: 'select',
        label: 'HTTP Method',
        section: 'basic',
        order: 4,
        validation: {
          required: true,
        },
        options: {
          items: [
            { value: 'GET', label: 'GET' },
            { value: 'POST', label: 'POST' },
            { value: 'PUT', label: 'PUT' },
            { value: 'PATCH', label: 'PATCH' },
            { value: 'DELETE', label: 'DELETE' },
          ],
        },
      },
      path: {
        type: 'text',
        label: 'Path',
        placeholder: '/api/resource/{id}',
        helperText: 'Must start with /',
        section: 'basic',
        order: 5,
        validation: {
          required: true,
          max: { value: 500, message: 'Path must be 500 characters or fewer' },
        },
      },
      path_parameters: {
        type: 'textarea',
        label: 'Path Parameters (JSON)',
        placeholder: '{"id": {"type": "string", "required": true}}',
        section: 'parameters',
        order: 6,
        options: {
          rows: 4,
          resize: 'vertical',
        },
      },
      query_parameters: {
        type: 'textarea',
        label: 'Query Parameters (JSON)',
        placeholder: '{"page": {"type": "integer", "default": 1}}',
        section: 'parameters',
        order: 7,
        options: {
          rows: 4,
          resize: 'vertical',
        },
      },
      headers: {
        type: 'textarea',
        label: 'Headers (JSON)',
        placeholder: '{"X-Custom-Header": "value"}',
        section: 'parameters',
        order: 8,
        options: {
          rows: 4,
          resize: 'vertical',
        },
      },
      example_params: {
        type: 'textarea',
        label: 'Example Parameters (JSON)',
        placeholder: '{"id": "12345", "page": 1}',
        section: 'parameters',
        order: 9,
        options: {
          rows: 4,
          resize: 'vertical',
        },
      },
      request_schema: {
        type: 'textarea',
        label: 'Request Schema (JSON)',
        placeholder: '{"type": "object", "properties": {...}}',
        section: 'schemas',
        order: 10,
        options: {
          rows: 5,
          resize: 'vertical',
        },
      },
      request_transform: {
        type: 'textarea',
        label: 'Request Transform (JSON)',
        placeholder: '{"mapping": {...}}',
        section: 'schemas',
        order: 11,
        options: {
          rows: 5,
          resize: 'vertical',
        },
      },
      response_schema: {
        type: 'textarea',
        label: 'Response Schema (JSON)',
        placeholder: '{"type": "object", "properties": {...}}',
        section: 'schemas',
        order: 12,
        options: {
          rows: 5,
          resize: 'vertical',
        },
      },
      response_transform: {
        type: 'textarea',
        label: 'Response Transform (JSON)',
        placeholder: '{"mapping": {...}}',
        section: 'schemas',
        order: 13,
        options: {
          rows: 5,
          resize: 'vertical',
        },
      },
      timeout_ms: {
        type: 'number',
        label: 'Timeout (ms)',
        placeholder: 'e.g. 30000',
        helperText: '1000-300000',
        section: 'settings',
        order: 14,
        options: {
          min: 1000,
          max: 300000,
        },
      },
      is_active: {
        type: 'checkbox',
        label: 'Active',
        section: 'settings',
        order: 15,
        defaultValue: true,
      },
    },

    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        order: 1,
      },
      {
        id: 'parameters',
        title: 'Parameters',
        order: 2,
        collapsible: true,
      },
      {
        id: 'schemas',
        title: 'Schemas & Transforms',
        order: 3,
        collapsible: true,
      },
      {
        id: 'settings',
        title: 'Settings',
        order: 4,
      },
    ],

    actions: createFormActions('Endpoint', isEdit),

    workflow: createFormWorkflow(`/admin/erp/registries/${registrySlug}`),

    layout: createFormLayout(),
  };
}
